import dayjs from "dayjs";
import { MOCK_TRIAGE_RESULTS } from "./mockTriageResults";
import type { TriageFlowStatus, TriageResultRow } from "./types";
import { TRIAGE_FLOW_STATUS_LABELS } from "./types";

export type TriageApprovalStatus = "unapproved" | "approved";

export type TriageResultDetail = {
  resultId: string;
  objectId: string;
  objectTypeName: string;
  objectSubTypeName: string;
  title: string;
  createdAt: string;
  matchedFlow: string;
  approvalStatus: TriageApprovalStatus;
  flowStatus: TriageFlowStatus;
  matchedRuleId: string;
  matchedConditions: string;
};

const DEFAULT_MATCHED_CONDITIONS =
  "Product Type = Large Molecule and Topic = Safety Concern and Health Authority = FDA";

const SHOWCASE_OBJECT_IDS = new Set(["INS28174", "INS28827"]);

function resolveTitle(row: TriageResultRow): string {
  if (SHOWCASE_OBJECT_IDS.has(row.objectId)) {
    return "Guidelines for Risk Management Plans for Pharmaceuticals - Guide-0020-10";
  }
  return row.title;
}

function buildDetail(row: TriageResultRow, index: number): TriageResultDetail {
  const matchedFlow = row.triageResult.trim() || "Manual Review";
  return {
    resultId: row.resultId,
    objectId: row.objectId,
    objectTypeName: row.objectTypeName,
    objectSubTypeName: row.objectSubTypeName,
    title: resolveTitle(row),
    createdAt: row.createDate,
    matchedFlow,
    approvalStatus: "unapproved",
    flowStatus: row.flowStatus === "none" ? "notStarted" : row.flowStatus,
    matchedRuleId: `TR${String((index % 99) + 1).padStart(2, "0")}`,
    matchedConditions: DEFAULT_MATCHED_CONDITIONS,
  };
}

export function getTriageResultDetail(
  resultId: string,
): TriageResultDetail | null {
  const trimmed = resultId.trim();
  if (!trimmed) return null;
  const index = MOCK_TRIAGE_RESULTS.findIndex(
    (row) => row.resultId === trimmed,
  );
  if (index < 0) return null;
  return buildDetail(MOCK_TRIAGE_RESULTS[index], index);
}

export function formatTriageDetailSummaryLine(detail: TriageResultDetail): string {
  const date = dayjs(detail.createdAt);
  const datePart = date.isValid()
    ? date.format("DD-MMM-YYYY HH:mm")
    : "-";
  const subType = detail.objectSubTypeName.trim() || "-";
  return `${detail.objectTypeName} · ${subType} · ${datePart}`;
}

export function formatTriageDetailFlowStatusLabel(
  status: TriageFlowStatus,
): string {
  if (status === "notStarted") return "Not Start";
  return TRIAGE_FLOW_STATUS_LABELS[status];
}

/** Plural label for the first breadcrumb segment (e.g. Insight → Insights). */
export function getTriageObjectTypeBreadcrumbLabel(
  objectTypeName: string,
): string {
  const trimmed = objectTypeName.trim();
  if (!trimmed) return "Insights";
  if (/s$/i.test(trimmed)) return trimmed;
  return `${trimmed}s`;
}
